/**
 * PostersTab — how many people scanned each street poster.
 *
 * Every poster's QR points at the marketing site with its own tag
 * (`valetnetwork.co/park?src=p1`). The site counts that tag once per browser
 * session and this reads the running totals back from `GET /api/scan`.
 *
 * Two things worth knowing before trusting a number here:
 *  - A scan is somebody pointing a phone at the poster. It is not a booking.
 *    The two are counted in completely different places and this tab only
 *    knows about the first one.
 *  - The count endpoint is public and unauthenticated, so it is inflatable by
 *    anyone who cares to. Fine for "which lamppost works", not fine as an
 *    input to a decision about money.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { styled } from '@mui/system';
import { getPosterScans } from '../../services/adminService';
import { POSTERS, summarise, whenAgo, shortDay, shortName } from './posterScanMath';
import { VALET, FONTS, TYPE } from '../../theme';

const DAYS_SHOWN = 14;

/* ------------------------------------------------------------------ styles */

const SectionTitle = styled(Typography)({ ...TYPE.sectionTitle, marginBottom: '4px' });
const Blurb = styled(Typography)({ ...TYPE.bodySmall, color: VALET.textSecondary });

const Card = styled(Box)({
    backgroundColor: VALET.surface,
    borderRadius: '14px',
    border: `1px solid ${VALET.border}`,
    boxShadow: VALET.shadow,
    padding: '22px 24px',
    '@media (max-width: 768px)': {
        padding: '16px 14px',
    },
});

const KpiRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
});

const KpiLabel = styled(Typography)({ ...TYPE.kpiLabel, marginBottom: '10px' });
const KpiValue = styled(Typography)({ ...TYPE.kpiValue });
const KpiNote = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '12.5px',
    color: VALET.textMuted,
    marginTop: '6px',
});

const Row = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 1.6fr) 1fr 96px 120px',
    alignItems: 'center',
    gap: '18px',
    padding: '18px 0',
    borderBottom: `1px solid ${VALET.border}`,
    '&:last-of-type': { borderBottom: 'none' },
    // Those four columns need ~616px before they start clipping, so on a phone
    // each poster becomes a stacked block instead of a row. The column headings
    // go with it — see HeadRow — since there are no columns left to head.
    '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
        alignItems: 'stretch',
        gap: '10px',
        padding: '16px 0',
    },
});

const HeadRow = styled(Row)({
    padding: '0 0 12px',
    borderBottom: `1px solid ${VALET.borderStrong || VALET.border}`,
    '@media (max-width: 768px)': {
        display: 'none',
    },
});

const HeadCell = styled(Typography)({
    ...TYPE.kpiLabel,
    marginBottom: 0,
});

const PosterName = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: VALET.textPrimary,
});

const Tag = styled('span')({
    fontFamily: FONTS.mono,
    fontSize: '11px',
    color: VALET.bronze,
    backgroundColor: VALET.bgMuted,
    border: `1px solid ${VALET.bronzeHairline}`,
    borderRadius: '5px',
    padding: '2px 6px',
    marginLeft: '8px',
});

const BarTrack = styled(Box)({
    height: '10px',
    borderRadius: '5px',
    backgroundColor: VALET.bgMuted,
    overflow: 'hidden',
});

const BarFill = styled(Box)({
    height: '100%',
    borderRadius: '5px',
    backgroundColor: VALET.green,
    transition: 'width 320ms ease',
});

const Count = styled(Typography)({
    fontFamily: FONTS.display,
    fontSize: '24px',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: VALET.textPrimary,
    textAlign: 'right',
});

const Muted = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    color: VALET.textMuted,
});

const Chart = styled(Box)({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    height: '120px',
    marginTop: '18px',
});

const ChartCol = styled(Box)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
});

const ChartBar = styled(Box)({
    width: '100%',
    borderRadius: '4px 4px 0 0',
    backgroundColor: VALET.green,
    minHeight: '2px',
});

const ChartLabel = styled(Typography)({
    fontFamily: FONTS.mono,
    fontSize: '9.5px',
    color: VALET.textMuted,
    whiteSpace: 'nowrap',
});

const Note = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    lineHeight: 1.6,
    color: VALET.textSecondary,
    marginTop: '18px',
});

const ErrorBox = styled(Typography)({
    color: VALET.danger,
    backgroundColor: VALET.dangerBg,
    fontSize: '13px',
    padding: '14px 18px',
    borderRadius: '8px',
    border: `1px solid ${VALET.danger}33`,
    fontWeight: 500,
    fontFamily: FONTS.sans,
});

/* --------------------------------------------------------------- component */

export default function PostersTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await getPosterScans());
        } catch (err) {
            setError(err.message || 'Could not load poster scans');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress sx={{ color: VALET.green }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <ErrorBox>{error}</ErrorBox>
                <Note>
                    The counts come from valetnetwork.co. If this keeps failing, the
                    marketing site is probably down or has not been deployed since poster
                    tracking was added.
                </Note>
                <Button onClick={load} sx={{ mt: 2, textTransform: 'none', color: VALET.green }}>
                    Try again
                </Button>
            </Box>
        );
    }

    const { rows: scans, total, lastWeek, best, most, perDay, peak } =
        summarise(data, { days: DAYS_SHOWN });

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '20px' }}>
                <Box>
                    <SectionTitle>Poster scans</SectionTitle>
                    <Blurb>
                        Each street poster has its own QR code. This is how many phones
                        scanned each one.
                    </Blurb>
                </Box>
                <Button onClick={load} sx={{ textTransform: 'none', color: VALET.green, fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <KpiRow>
                <Card>
                    <KpiLabel>Scans, all time</KpiLabel>
                    <KpiValue>{total.toLocaleString()}</KpiValue>
                    <KpiNote>Since the posters went up</KpiNote>
                </Card>
                <Card>
                    <KpiLabel>Scans, last 7 days</KpiLabel>
                    <KpiValue>{lastWeek.toLocaleString()}</KpiValue>
                    <KpiNote>{lastWeek === 0 ? 'Nobody scanned this week' : 'Counted in New York time'}</KpiNote>
                </Card>
                <Card>
                    <KpiLabel>Best poster</KpiLabel>
                    <KpiValue>{total === 0 ? '—' : shortName(best.src)}</KpiValue>
                    <KpiNote>
                        {total === 0
                            ? 'No scans yet'
                            : `${best.count.toLocaleString()} of ${total.toLocaleString()} scans`}
                    </KpiNote>
                </Card>
            </KpiRow>

            <Card>
                <HeadRow>
                    <HeadCell>Poster</HeadCell>
                    <HeadCell>Share of scans</HeadCell>
                    <HeadCell sx={{ textAlign: 'right' }}>Scans</HeadCell>
                    <HeadCell>Last scan</HeadCell>
                </HeadRow>

                {scans.map((s) => {
                    const meta = POSTERS[s.src];
                    return (
                        <Row key={s.src}>
                            <Box sx={{ minWidth: 0 }}>
                                <PosterName>
                                    {meta ? meta.name : s.src}
                                    <Tag>?src={s.src}</Tag>
                                </PosterName>
                                <Muted sx={{ mt: '3px' }}>
                                    {meta ? meta.blurb : 'Unrecognised tag — added after this page was written'}
                                </Muted>
                            </Box>
                            <BarTrack>
                                <BarFill sx={{ width: `${Math.round((s.count / most) * 100)}%` }} />
                            </BarTrack>
                            <Count>{s.count.toLocaleString()}</Count>
                            <Muted>{whenAgo(s.lastAt)}</Muted>
                        </Row>
                    );
                })}
            </Card>

            <Card sx={{ mt: '20px' }}>
                <SectionTitle>Scans a day, last {DAYS_SHOWN} days</SectionTitle>
                <Blurb>All posters added together.</Blurb>
                <Chart>
                    {perDay.map(({ day, n }) => (
                        <ChartCol key={day}>
                            <Muted sx={{ fontSize: '11px' }}>{n || ''}</Muted>
                            <ChartBar sx={{ height: `${Math.round((n / peak) * 84)}%`, opacity: n ? 1 : 0.18 }} />
                            <ChartLabel>{shortDay(day)}</ChartLabel>
                        </ChartCol>
                    ))}
                </Chart>
            </Card>

            <Note>
                A scan means somebody pointed their phone at the poster and the page
                opened. It does not mean they booked — bookings are on the Business tab.
                Counted once per phone per visit, so a person reloading while they read
                only counts once.
                {total === 0 && ' Nothing has been scanned yet. If posters are already up, scan one yourself and refresh this page — it should show 1.'}
            </Note>
        </Box>
    );
}
